'use client'

import { useId, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface AnimatedBiomoLogoProps {
  className?: string
  shimmer?: boolean
}

export function AnimatedBiomoLogo({ className, shimmer = true }: AnimatedBiomoLogoProps) {
  const uid = useId().replace(/:/g, '')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    requestAnimationFrame(() => setLoaded(true))
  }, [])

  const cp = (n: number) => `${uid}cp${n}`
  const cpUrl = (n: number) => `url(#${cp(n)})`

  return (
    <div className={cn('relative inline-block shrink-0 overflow-hidden', loaded && 'biomo-loaded', className)} style={{ aspectRatio: '976 / 1060' }}>
      <svg
        viewBox="122 80 976 1060"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
      >
        <defs>
          <clipPath id={cp(1)}><path d="M522.52,636.71v-285.47h-228.38v-114.19h-57.09v399.66h285.47ZM294.14,408.33h171.28v171.28h-171.28v-171.28Z" /></clipPath>
          <clipPath id={cp(2)}><rect x="351.24" y="465.43" width="57.09" height="57.09" /></clipPath>
          <clipPath id={cp(3)}><rect x="351.24" y="808" width="57.09" height="171.28" /></clipPath>
          <clipPath id={cp(4)}><rect x="351.24" y="237.05" width="171.28" height="57.09" /></clipPath>
          <clipPath id={cp(5)}><rect x="465.43" y="808" width="57.09" height="171.28" /></clipPath>
          <clipPath id={cp(6)}><polygon points="237.05 693.81 237.05 979.28 294.14 979.28 294.14 750.9 579.62 750.9 579.62 979.28 636.71 979.28 636.71 693.81 237.05 693.81" /></clipPath>
          <clipPath id={cp(7)}><rect x="579.62" y="237.05" width="57.09" height="57.09" /></clipPath>
          <clipPath id={cp(8)}><rect x="579.62" y="351.24" width="57.09" height="285.47" /></clipPath>
          <clipPath id={cp(9)}><rect x="807.99" y="465.43" width="57.09" height="57.09" /></clipPath>
          <clipPath id={cp(10)}><rect x="807.99" y="808" width="57.09" height="57.09" /></clipPath>
          <clipPath id={cp(11)}><rect x="693.81" y="237.05" width="285.47" height="57.09" /></clipPath>
          <clipPath id={cp(12)}><path d="M693.81,636.71h285.47v-285.47h-285.47v285.47ZM922.18,579.62h-171.28v-171.28h171.28v171.28Z" /></clipPath>
          <clipPath id={cp(13)}><path d="M693.81,979.28h285.47v-285.47h-285.47v285.47ZM922.18,922.19h-171.28v-171.28h171.28v171.28Z" /></clipPath>
        </defs>

        {/* Main structure (st5) — adapts light/dark */}
        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M522.52,636.71v-285.47h-228.38v-114.19h-57.09v399.66h285.47ZM294.14,408.33h171.28v171.28h-171.28v-171.28Z" />
        <g style={{ clipPath: cpUrl(1) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M379.79,94.3l342.57,342.57-342.57,342.57L37.23,436.87,379.79,94.3Z" /></g>

        {/* Accent cyan→purple-400 (st14) + purple (st9) pairs */}
        <path className="biomo-st14 fill-purple-400" d="M408.33,465.43v57.09h-57.09v-57.09h57.09Z" />
        <g><rect className="biomo-st9 fill-purple-500" x="351.24" y="465.43" width="57.09" height="57.09" /><g style={{ clipPath: cpUrl(2) }}><path className="biomo-st9 fill-purple-500" d="M379.78,436.88l57.09,57.09-57.09,57.09-57.09-57.09,57.09-57.09Z" /></g></g>

        <path className="biomo-st14 fill-purple-400" d="M408.33,808v171.28h-57.09v-171.28h57.09Z" />
        <g><rect className="biomo-st9 fill-purple-500" x="351.24" y="808" width="57.09" height="171.28" /><g style={{ clipPath: cpUrl(3) }}><path className="biomo-st9 fill-purple-500" d="M379.78,779.45l114.18,114.18-114.18,114.18-114.18-114.18,114.18-114.18Z" /></g></g>

        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M522.52,237.05v57.09h-171.28v-57.09h171.28Z" />
        <g style={{ clipPath: cpUrl(4) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M436.88,151.43l114.18,114.18-114.18,114.18-114.18-114.18,114.18-114.18Z" /></g>

        <path className="biomo-st14 fill-purple-400" d="M522.52,808v171.28h-57.09v-171.28h57.09Z" />
        <g><rect className="biomo-st9 fill-purple-500" x="465.43" y="808" width="57.09" height="171.28" /><g style={{ clipPath: cpUrl(5) }}><path className="biomo-st9 fill-purple-500" d="M493.96,779.45l114.18,114.18-114.18,114.18-114.18-114.18,114.18-114.18Z" /></g></g>

        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M237.05,693.81v285.47h57.09v-228.38h285.48v228.38h57.09v-285.47H237.05Z" />
        <g style={{ clipPath: cpUrl(6) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M436.88,493.97l342.57,342.57-342.57,342.57L94.31,836.54l342.57-342.57Z" /></g>

        <path className="biomo-st14 fill-purple-400" d="M636.71,237.05v57.09h-57.09v-57.09h57.09Z" />
        <g><rect className="biomo-st9 fill-purple-500" x="579.62" y="237.05" width="57.09" height="57.09" /><g style={{ clipPath: cpUrl(7) }}><path className="biomo-st9 fill-purple-500" d="M608.16,208.49l57.09,57.09-57.09,57.09-57.09-57.09,57.09-57.09Z" /></g></g>

        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M636.71,351.24v285.47h-57.09v-285.47h57.09Z" />
        <g style={{ clipPath: cpUrl(8) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M608.17,322.69l171.28,171.28-171.28,171.29-171.29-171.29,171.29-171.28Z" /></g>

        <path className="biomo-st14 fill-purple-400" d="M865.07,465.43v57.09h-57.09v-57.09h57.09Z" />
        <g><rect className="biomo-st9 fill-purple-500" x="807.99" y="465.43" width="57.09" height="57.09" /><g style={{ clipPath: cpUrl(9) }}><path className="biomo-st9 fill-purple-500" d="M836.52,436.89l57.09,57.09-57.09,57.09-57.09-57.09,57.09-57.09Z" /></g></g>

        <path className="biomo-st14 fill-purple-400" d="M865.07,808v57.09h-57.09v-57.09h57.09Z" />
        <g><rect className="biomo-st9 fill-purple-500" x="807.99" y="808" width="57.09" height="57.09" /><g style={{ clipPath: cpUrl(10) }}><path className="biomo-st9 fill-purple-500" d="M836.54,779.46l57.09,57.09-57.09,57.09-57.09-57.09,57.09-57.09Z" /></g></g>

        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M979.28,237.05v57.09h-285.47v-57.09h285.47Z" />
        <g style={{ clipPath: cpUrl(11) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M836.54,94.32l171.29,171.28-171.29,171.29-171.28-171.28,171.28-171.29Z" /></g>

        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M693.81,636.71h285.47v-285.47h-285.47v285.47ZM922.18,579.62h-171.28v-171.28h171.28v171.28Z" />
        <g style={{ clipPath: cpUrl(12) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M836.54,208.51l285.47,285.47-285.47,285.47-285.47-285.47,285.47-285.47Z" /></g>

        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M693.81,979.28h285.47v-285.47h-285.47v285.47ZM922.18,922.19h-171.28v-171.28h171.28v171.28Z" />
        <g style={{ clipPath: cpUrl(13) }}><path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M836.54,551.07l285.47,285.47-285.47,285.47-285.47-285.47,285.47-285.47Z" /></g>

        {/* Outer frame */}
        <path className="biomo-st5 fill-indigo-950 dark:fill-white" d="M122.86,1093.47h970.61V122.86H122.86v970.61ZM1036.37,1036.37H179.96V179.96h856.42" />
      </svg>

      {/* Shimmer sweep overlay */}
      {shimmer && <div className="biomo-logo-shimmer" />}
    </div>
  )
}
